import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ClientNotificationHandlerService, CommonUtilityLib, FilterFormDataModel, ValueListsService } from '@thehubz/client/core';
import { ProjectsApiService, RelationsApiService, Team, TeamsApiService, WorkflowsApiService } from '@thehubz/client/data-access-api';
import { AnyType, AssetMimeType, AssetType, uniqueMimeTypes } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class FilterFormService {
  totalRequests = 0;
  teamList: Team[] = [];

  constructor(
    private teamsApiService: TeamsApiService,
    private commonUtilityLib: CommonUtilityLib,
    private projectsService: ProjectsApiService,
    private valueListsService: ValueListsService,
    private workflowsApiService: WorkflowsApiService,
    private relationsApiService: RelationsApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {}

  /**
   * fetch companies
   */
  async getClients(filterProjectFormData: FilterFormDataModel, roles: string[]) {
    this.totalRequests++;
    const response = await lastValueFrom(this.valueListsService.listUserClients(roles));
    if (response && response.length > 0) {
      this.totalRequests--;
      const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'client');
      if (index !== -1) filterProjectFormData.formFields[index]['options'] = response;
    } else {
      this.totalRequests--;
    }
  }

  /**
   * fetch companies
   */
  async getBrands(filterProjectFormData: FilterFormDataModel, roles: string[]) {
    this.totalRequests++;
    const response: AnyType = await lastValueFrom(this.valueListsService.listUserClients(roles));
    if (response && response.length > 0) {
      this.totalRequests--;
      const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'brand');
      if (index !== -1 && response[0].brands) filterProjectFormData.formFields[index]['options'] = response[0].brands;
    } else {
      this.totalRequests--;
    }
  }

  /**
   * fetch agencies
   */
  async getAgencies(filterProjectFormData: FilterFormDataModel, roles: string[]) {
    this.totalRequests++;
    const response = await lastValueFrom(this.valueListsService.listUserAgencies(roles));
    if (response && response.length > 0) {
      this.totalRequests--;
      const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'agency');
      if (index !== -1) filterProjectFormData.formFields[index]['options'] = response.filter((res) => res.clients.length > 0);
    } else {
      this.totalRequests--;
    }
  }

  /**
   * fetch relation type   */
  async getRelationType(filterProjectFormData: FilterFormDataModel) {
    this.totalRequests++;
    const response = await lastValueFrom(this.relationsApiService.relationsControllerFindAllRelationTypes());
    if (response && response.length > 0) {
      this.totalRequests--;
      const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'relationType');
      if (index !== -1) filterProjectFormData.formFields[index]['options'] = response.map((res) => ({ _id: res._id, name: res.description }));
    } else {
      this.totalRequests--;
    }
  }

  /**
   * Fetch team list
   */
  async fetchTeamsList() {
    this.totalRequests++;
    const response = await lastValueFrom(this.teamsApiService.teamsControllerFindAllTeams(true));
    if (response && response['result']) {
      this.totalRequests--;
      this.teamList = response['result'];
    } else {
      this.totalRequests--;
    }
  }

  /**
   * Fetch project list based on the ACB combination
   * @param filterProjectFormData
   * @param filterForm
   */
  async fetchACBProjectList(filterProjectFormData: FilterFormDataModel, filterData: AnyType) {
    const filterObject: AnyType = {
      filters: {},
      sort: { updatedAt: -1 },
      skip: 0,
    };
    const pickedACB = filterData ? (({ agency, client, brand, status }) => ({ agency, client, brand, status }))(filterData) : ({} as AnyType);
    filterObject['filters']['agency._id'] = pickedACB['agency'] ? (pickedACB['agency']._id ?? pickedACB['agency'].id) : undefined;
    filterObject['filters']['brand._id'] = pickedACB['brand'] ? (pickedACB['brand']._id ?? pickedACB['brand'].id) : undefined;
    filterObject['filters']['client._id'] = pickedACB['client'] ? (pickedACB['client']._id ?? pickedACB['client'].id) : undefined;
    if (pickedACB['status'] && pickedACB['status'].id === 'Closed') {
      filterObject['filters']['status'] = pickedACB['status'] ? pickedACB['status'].id : undefined;
    }
    await this.fetchAllProjectList(filterProjectFormData, filterObject);
  }

  /**
   * Fetch all project list
   */
  async fetchAllProjectList(filterProjectFormData: FilterFormDataModel, filterObject: AnyType) {
    const response = await lastValueFrom(this.projectsService.projectsControllerFindAll(JSON.stringify(filterObject)));
    const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'project');
    if (response && response['result'] && response['result'].length > 0) {
      const projectData = response['result'];
      const projectListData: AnyType = [];
      projectData.forEach((project) => {
        projectListData.push({ id: project?._id, name: project?.name });
      });
      if (index !== -1) filterProjectFormData.formFields[index]['options'] = projectListData;
    } else {
      filterProjectFormData.formFields[index]['options'] = [];
    }
  }

  /**
   * Find all workflow
   */
  getWorkflowList(filterProjectFormData: FilterFormDataModel) {
    this.totalRequests++;
    // TODO: replace call to workflowsApiService with a valueListsService call
    this.workflowsApiService.workflowsControllerXFindAll().subscribe({
      next: (response: AnyType) => {
        this.totalRequests--;
        const options: AnyType = [];
        const workFlowData = response['result'];
        if (workFlowData.length > 0) {
          workFlowData.forEach((element: AnyType) => {
            options.push({ id: element['_id'], name: element['name'] });
          });
        }
        const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'workflowType');
        if (index !== -1) filterProjectFormData.formFields[index]['options'] = options;
      },
      error: (err) => {
        this.totalRequests--;
        this.clientNotificationHandlerService.errorNotification(err);
      },
    });
  }

  async getCreateByUserList(filterProjectFormData: FilterFormDataModel) {
    this.totalRequests++;
    const usersLikeMe = await lastValueFrom(this.valueListsService.listUsersLikeMe(['global.collab'], false, true));
    if (usersLikeMe && usersLikeMe.length > 0) {
      this.totalRequests--;
      const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'createdBy');
      if (index !== -1)
        filterProjectFormData.formFields[index]['options'] = usersLikeMe.map((user) => ({
          _id: user.id,
          name: user.name,
        }));
    } else {
      this.totalRequests--;
    }
  }

  /**
   * Populate team list based on agency ID
   * @param eventValueId
   */
  async fetchTeamsByAgency(filterProjectFormData: FilterFormDataModel, eventValueId: string, filterForm: UntypedFormGroup, singleValueSource = false) {
    const teamsByAgency = this.teamList.filter((team) => team.agency._id.toString() === eventValueId);
    const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'currentlyWithTeam');
    if (index !== -1) {
      filterProjectFormData.formFields[index]['options'] = [];
      filterProjectFormData.formFields[index]['options'] = teamsByAgency;
    }
    if (!singleValueSource) filterForm.controls['currentlyWithTeam']?.setValue('');
  }

  /**
   * Fill user list based on selected team
   * @param selectedOption
   */
  usersCurrentlyWithTeam(filterProjectFormData: FilterFormDataModel, filterForm: UntypedFormGroup, selectedOption: AnyType, matchingKey: string) {
    const fieldIndex = this.commonUtilityLib.findArrayIndex(filterProjectFormData, matchingKey);
    filterProjectFormData.formFields[fieldIndex]['options'] = [];
    if (selectedOption?.users && selectedOption?.users.length > 0) {
      selectedOption.users.forEach((user: AnyType) => {
        filterProjectFormData.formFields[fieldIndex]['options'].push({
          _id: user?.user ? user?.user?._id : '',
          name: user?.user ? user?.user.firstName + ' ' + user?.user.lastName : '',
        });
      });
    }
    if (matchingKey === 'currentlyWithUser') {
      filterProjectFormData.formFields[fieldIndex]['options'].push({
        _id: '101',
        name: 'Unassigned',
      });
    }
    filterForm.controls[matchingKey].setValue('');
  }

  /**
   * fetch companies list based on agency selection
   * @param agencyWithClients
   */
  fetchCompaniesByAgency(filterProjectFormData: FilterFormDataModel, filterForm: UntypedFormGroup, agencyWithClients: AnyType, singleValueSource = false) {
    if (agencyWithClients) {
      const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'client');
      if (index !== -1) filterProjectFormData.formFields[index]['options'] = agencyWithClients.clients;
      if (!singleValueSource) filterForm.controls['client'].setValue('');
    }
  }

  /**
   * Update file type form field when file type changes
   * @param filterProjectFormData
   * @param type
   */
  updateFileType(filterProjectFormData: FilterFormDataModel, type: string) {
    const index = this.commonUtilityLib.findArrayIndex(filterProjectFormData, 'fileType');
    if (index !== -1) {
      if (AssetType[type]) {
        const mimeType = uniqueMimeTypes(AssetType[type].mimeTypes);
        filterProjectFormData.formFields[index]['options'] = mimeType;
      } else {
        filterProjectFormData.formFields[index]['options'] = AssetMimeType.flat(2);
      }
    }
  }
}
